import type { CSSProperties, ReactNode } from 'react'
import { AlertTriangle, CheckCircle, Info, X, XCircle } from 'lucide-react'
import { cn } from '../lib'
import { designTokens } from '../lib/design-tokens'
import { typographyStyle, withAlpha } from '../lib/catalog'

type AlertVariant = 'success' | 'error' | 'warning' | 'info'

type AlertBannerProps = {
  variant: AlertVariant
  message: string
  title?: string
  dismissible?: boolean
  onDismiss?: () => void
  action?: ReactNode
  className?: string
}

const variantStyles: Record<
  AlertVariant,
  { background: string; borderColor: string; color: string; icon: typeof CheckCircle }
> = {
  success: {
    background: designTokens.colors.background.successSoft,
    borderColor: designTokens.colors.text.success,
    color: designTokens.colors.text.successStrong,
    icon: CheckCircle,
  },
  error: {
    background: withAlpha(designTokens.colors.semantic.error, 0.12),
    borderColor: designTokens.colors.semantic.error,
    color: designTokens.colors.semantic.error,
    icon: XCircle,
  },
  warning: {
    background: withAlpha(designTokens.colors.text.additional, 0.15),
    borderColor: designTokens.colors.text.additional,
    color: designTokens.colors.text.additional,
    icon: AlertTriangle,
  },
  info: {
    background: withAlpha(designTokens.colors.accent.mobileBlue, 0.12),
    borderColor: designTokens.colors.accent.mobileBlue,
    color: designTokens.colors.accent.mobileBlue,
    icon: Info,
  },
}

export function AlertBanner({
  variant,
  message,
  title,
  dismissible = false,
  onDismiss,
  action,
  className,
}: AlertBannerProps) {
  const palette = variantStyles[variant]
  const Icon = palette.icon
  const titleStyle: CSSProperties = typographyStyle(designTokens.typography.body.mdSemibold)
  const messageStyle: CSSProperties = typographyStyle(designTokens.typography.body.sm)

  return (
    <div
      className={cn('flex w-full items-start gap-3 rounded-xl border px-4 py-3', className)}
      style={{
        background: palette.background,
        borderColor: palette.borderColor,
        color: palette.color,
      }}
      role="status"
    >
      <span className="mt-0.5 inline-flex h-5 w-5 items-center justify-center">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div className="flex flex-1 flex-col gap-1">
        {title ? (
          <span className="text-sm font-semibold" style={titleStyle}>
            {title}
          </span>
        ) : null}
        <span className="text-sm" style={messageStyle}>
          {message}
        </span>
        {action ? <div className="pt-2">{action}</div> : null}
      </div>
      {dismissible ? (
        <button
          type="button"
          aria-label="Dismiss alert"
          className="inline-flex items-center justify-center text-current/70 transition hover:text-current"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  )
}
